import { getAPIErrorReason } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { captureException } from './logService';
import MessageGroupCacheService from './messageGroupCacheService';
import { rootStore } from '../mobx/rootStore';
import { createObservableChatGroup } from '../mobx/models/chatMessage';
import { ChatGroupInterface } from '../interfaces/chatMessageInterface';
import { dispatchChatMessageError } from './chatMessageUtilService';
import {
  applySignalPreview,
  deriveConversationRow,
  getConversations,
  mergeConversationRows,
  refreshOpenChatGroupMessages,
  refreshProjectDiscussion,
  SignalLastMessage
} from './conversationSyncService';

// Firestore trigger doc `messages/{workspaceId}_{userId}`; `conversations` lists changed ids. An
// entry is the conversation type, or `{ type, last }` when a new message was sent.
export interface MessageSyncSignal {
  conversations?: Record<string, number | { type?: number; last?: SignalLastMessage }>;
}

// Rows updated from a signal preview get their exact values (unread count) from one batched fetch
// a little later, instead of one fetch per message.
const ROW_REFRESH_DELAY_MS = 10 * 1000;
const pendingRowRefresh = new Set<string>();
let rowRefreshTimer: ReturnType<typeof setTimeout> | undefined;
let rowRefreshWorkspaceId: string | undefined;

const timeOf = (value?: string | null) => (value ? new Date(value).getTime() : 0);

// Drops a pending row refresh (logout).
export const cancelRowRefresh = () => {
  if (rowRefreshTimer) clearTimeout(rowRefreshTimer);
  rowRefreshTimer = undefined;
  pendingRowRefresh.clear();
  rowRefreshWorkspaceId = undefined;
};

const scheduleRowRefresh = (ids: string[]) => {
  if (ids.length === 0) return;
  const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;
  if (!workspaceId) return;
  if (rowRefreshWorkspaceId !== workspaceId) {
    pendingRowRefresh.clear();
    rowRefreshWorkspaceId = workspaceId;
  }
  ids.forEach((id) => pendingRowRefresh.add(id));
  if (rowRefreshTimer) return;
  rowRefreshTimer = setTimeout(async () => {
    rowRefreshTimer = undefined;
    const batch = Array.from(pendingRowRefresh);
    pendingRowRefresh.clear();
    const isSameWorkspace = () => rootStore.workspaceStore.currentWorkspace?.id === workspaceId;
    if (!isSameWorkspace() || rootStore.chatMessageStore.chatGroups.length === 0) return;
    // A failure is left to the next signal or app start (no retry loop while offline).
    const rows = await getConversations(batch);
    if (!rows || !isSameWorkspace()) return;
    // A row that got a newer message while this request was in flight keeps its newer preview; that
    // message's signal already queued the next batch.
    const local = new Map(rootStore.chatMessageStore.chatGroups.map((group) => [group.id, group]));
    const isOlder = (row: ChatGroupInterface) => timeOf(row.lastMessageTime) < timeOf(local.get(row.id)?.lastMessageTime);
    const keptIds = rows.filter(isOlder).map((row) => row.id);
    await mergeConversationRows(
      batch.filter((id) => !keptIds.includes(id)),
      rows.filter((row) => !isOlder(row))
    );
  }, ROW_REFRESH_DELAY_MS);
};

/**
 * @desc Loads the lean, server-sorted conversation list (preview, unread, pin) into the store.
 */
export const getChatGroupList = async (shared = false) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const groups = await getConversations(undefined, shared);
    if (groups) {
      rootStore.chatMessageStore.setChatGroups(groups);
      // Cache keeps groups (with members) for lookups such as resolving an existing DM.
      await MessageGroupCacheService.getInstance()?.setMessageGroups(groups);
    }
    return groups;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get message groups please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Background sync, driven by the Firestore trigger doc. When the signal names the changed
 * conversations, refreshes only those sidebar rows and the open chat/project if it changed;
 * otherwise refreshes the whole conversation list and whatever is open.
 * @returns {Promise<boolean>} true when the sync completed successfully, false otherwise.
 */
export const syncMessagesAndGroupsInBackground = async (signal?: MessageSyncSignal | null): Promise<boolean> => {
  try {
    // Without a signal (startup) any in-flight identical request will do; with one, requests must
    // start after the change.
    const shared = !signal;
    const changedIds = Object.keys(signal?.conversations || {});
    const isChanged = (id: string) => changedIds.length === 0 || changedIds.includes(id);
    // Conversations left this session re-check on open only when a change named them.
    rootStore.chatMessageStore.markOpenChatMemoryDirty(changedIds.length > 0 ? changedIds : undefined);

    // Refreshes the open chat/project when it changed; resolves the ids that synced.
    const refreshOpenConversations = async () => {
      const synced: string[] = [];
      try {
        const { currentChatGroup } = rootStore.chatMessageStore;
        if (currentChatGroup?.id && isChanged(currentChatGroup.id)) {
          if (await refreshOpenChatGroupMessages(currentChatGroup.id, currentChatGroup.type, shared)) {
            synced.push(currentChatGroup.id);
          } else {
            rootStore.chatMessageStore.markOpenChatStale(currentChatGroup.id);
          }
        }
        const { activeDiscussionProjectId } = rootStore.projectStore;
        if (activeDiscussionProjectId && isChanged(activeDiscussionProjectId)) {
          if (await refreshProjectDiscussion(activeDiscussionProjectId)) synced.push(activeDiscussionProjectId);
        }
      } catch (e) {
        captureException(e);
      }
      return synced;
    };

    // Row-level merge needs a loaded list; otherwise load the whole list once.
    if (changedIds.length > 0 && rootStore.chatMessageStore.chatGroups.length > 0) {
      // Rows of the open chat/project are built from their synced messages; only the rest are fetched.
      const synced = await refreshOpenConversations();
      const derived = synced
        .map(deriveConversationRow)
        .filter((row): row is ChatGroupInterface => !!row && changedIds.includes(row.id));
      const remaining = changedIds.filter((id) => !derived.some((row) => row.id === id));
      // A new message carried in the signal updates its row now; the exact row is fetched later.
      const previews = remaining
        .map((id) => {
          const entry = signal?.conversations?.[id];
          return typeof entry === 'object' && entry?.last ? applySignalPreview(id, entry.last) : undefined;
        })
        .filter((row): row is ChatGroupInterface => !!row);
      const fetchIds = remaining.filter((id) => !previews.some((row) => row.id === id));
      const rows = fetchIds.length > 0 ? await getConversations(fetchIds) : [];
      if (rows === undefined) return false;
      await mergeConversationRows(changedIds, [...rows, ...derived, ...previews]);
      scheduleRowRefresh(previews.map((row) => row.id));
    } else {
      const groups = await getChatGroupList(shared);
      if (groups === undefined) return false;
      await refreshOpenConversations();
    }
    return true;
  } catch (e) {
    captureException(e);
    return false;
  }
};

/**
 * @desc After the user's own send/reaction (the backend signals other members, not the actor):
 * refreshes only that conversation's sidebar row and its open chat/project discussion.
 */
export const syncConversation = (conversationId: string, type: number) =>
  syncMessagesAndGroupsInBackground({ conversations: { [conversationId]: type } });

/**
 * @desc Message - set last read time for message group
 * @param {*}
 */
export const setLastReadForDiscussion = async (groupId?: string) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const { currentChatGroup, chatGroups } = rootStore.chatMessageStore;
    const targetGroup = groupId
      ? chatGroups?.find((group) => group?.id === groupId) ||
        (currentChatGroup?.id === groupId ? currentChatGroup : undefined)
      : currentChatGroup || undefined;
    if (targetGroup?.id) {
      // Lean groups carry no messages; fall back to the server-provided lastMessageTime.
      const lastReadTime =
        targetGroup?.messages?.length > 0
          ? targetGroup?.messages[targetGroup?.messages.length - 1].createdOn
          : targetGroup?.lastMessageTime || null;
      const currentUserId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
      // Already seen up to the last message (or nothing to read): skip the request and its DB writes.
      const seenTime = currentUserId ? new Date(targetGroup?.lastSeen?.[currentUserId] || 0).getTime() : 0;
      if (!lastReadTime || seenTime >= new Date(lastReadTime).getTime()) return true;
      // Applies changes to this conversation's sidebar rows, on the store's current lists.
      const updateRows = (changes: (group: ChatGroupInterface) => Partial<ChatGroupInterface>) => {
        const store = rootStore.chatMessageStore;
        const apply = (groups: ChatGroupInterface[]) =>
          groups?.map((group) =>
            group?.id === targetGroup.id ? createObservableChatGroup({ ...group, ...changes(group) }) : group
          );
        store.setChatGroups(apply(store.chatGroups));
        store.setPinChatGroups(apply(store.pinChatGroups));
        store.setUnpinChatGroups(apply(store.unpinChatGroups));
      };
      const withLastSeen = (group: ChatGroupInterface) => ({
        ...group?.lastSeen,
        ...(currentUserId ? { [currentUserId]: lastReadTime } : {})
      });
      // Clear the badge now, but record lastSeen only once the server has it: a failed request is
      // then retried on the next click instead of being skipped as already seen.
      if (targetGroup?.hasUnreadMessages) updateRows(() => ({ hasUnreadMessages: false, unreadCount: 0 }));
      const payload = {
        type: targetGroup?.type,
        time_get_from_payload: lastReadTime
      };
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/discussions/${targetGroup?.id}`, payload);
      if (response) {
        updateRows((group) => ({ hasUnreadMessages: false, unreadCount: 0, lastSeen: withLastSeen(group) }));
        // Current state, not the copy from before the request (messages may have arrived meanwhile).
        const openGroup = rootStore.chatMessageStore.currentChatGroup;
        if (openGroup?.id === targetGroup?.id) {
          rootStore.chatMessageStore.setCurrentChatGroup({
            ...openGroup,
            hasUnreadMessages: false,
            unreadCount: 0,
            lastSeen: withLastSeen(openGroup)
          });
        }
        return true;
      }
      return false;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to set last read for message group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};
